import React, { useMemo, useRef, useState } from 'react'
import { Alert, Input, Modal, Spin, Tooltip, Typography, message } from 'antd'
import {
  FlagOutlined,
  LoadingOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import { Stack } from '@sureco/design-system'
import type { DefinitionManifestEntry } from '../hooks/useCustomerConfigurationInventory'
import type { ConfigDefinition } from '../hooks/useConfigurationSchema'
import useUpsertCustomerConfiguration from '../hooks/useUpsertCustomerConfiguration'
import epsClient from 'enrollment-platform/api/eps.client'

const { Text } = Typography

interface CreateConfigurationPickerModalProps {
  open: boolean
  customerId: string
  availableDefinitions: DefinitionManifestEntry[]
  onClose: () => void
  onAfterClose: () => void
  onSelectConfig: (definition: DefinitionManifestEntry) => void
}

function TypeIcon({ type }: { type: 'feature-flag' | 'configuration' }) {
  if (type === 'feature-flag') {
    return (
      <Tooltip title="Feature flag">
        <FlagOutlined
          style={{ color: '#2563EB', fontSize: 16, flexShrink: 0 }}
        />
      </Tooltip>
    )
  }

  return (
    <Tooltip title="Configuration">
      <SettingOutlined
        style={{ color: '#7C3AED', fontSize: 16, flexShrink: 0 }}
      />
    </Tooltip>
  )
}

function DefinitionRow({
  definition,
  isDisabled,
  onClick,
}: {
  definition: DefinitionManifestEntry
  isDisabled: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      style={{
        alignItems: 'flex-start',
        background: 'none',
        border: '1px solid #E8F3F5',
        borderRadius: 8,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        gap: 12,
        opacity: isDisabled ? 0.5 : 1,
        padding: '10px 14px',
        textAlign: 'left',
        transition: 'background 0.15s',
        width: '100%',
      }}
      onMouseEnter={(e) => {
        if (!isDisabled) {
          e.currentTarget.style.background = '#F5FAFB'
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'none'
      }}
    >
      <span style={{ marginTop: 2 }}>
        <TypeIcon type={definition.type} />
      </span>
      <Stack space={4} style={{ flex: 1, minWidth: 0 }}>
        <Text strong style={{ color: '#103044', fontSize: 14 }}>
          {definition.title}
        </Text>
        <span
          style={{
            background: '#EDF2F7',
            borderRadius: 4,
            color: '#5B7083',
            display: 'inline-block',
            fontFamily: 'monospace',
            fontSize: 11,
            padding: '1px 6px',
          }}
        >
          {definition.key}
        </span>
        {definition.description.length > 0 && (
          <Text style={{ color: '#5B7083', fontSize: 13 }}>
            {definition.description}
          </Text>
        )}
      </Stack>
    </button>
  )
}

export default function CreateConfigurationPickerModal({
  open,
  customerId,
  availableDefinitions,
  onClose,
  onAfterClose,
  onSelectConfig,
}: CreateConfigurationPickerModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [creatingFlagKey, setCreatingFlagKey] = useState<string | null>(null)
  const [flagError, setFlagError] = useState<string | null>(null)

  const { mutate, isPending: isMutating } = useUpsertCustomerConfiguration()

  // A per-click cancellation token. When the user closes the modal during the
  // schema fetch, we mark the in-flight flow as cancelled so the subsequent
  // `mutate` call is skipped. Without this, closing the modal mid-fetch would
  // still result in a flag being created on the server.
  const activeFlagFlowRef = useRef<{ cancelled: boolean } | null>(null)

  const filteredDefinitions = useMemo(() => {
    const term = searchTerm.toLowerCase().trim()
    if (term.length === 0) {
      return availableDefinitions
    }
    return availableDefinitions.filter(
      (d) =>
        d.title.toLowerCase().includes(term) ||
        d.key.toLowerCase().includes(term) ||
        d.description.toLowerCase().includes(term)
    )
  }, [availableDefinitions, searchTerm])

  const groupedByCategory = useMemo(() => {
    const groups = new Map<string, DefinitionManifestEntry[]>()
    for (const definition of filteredDefinitions) {
      const group = groups.get(definition.category) ?? []
      group.push(definition)
      groups.set(definition.category, group)
    }
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredDefinitions])

  const isCreatingFlag = creatingFlagKey !== null

  async function handleFlagClick(definition: DefinitionManifestEntry) {
    const flowToken = { cancelled: false }
    activeFlagFlowRef.current = flowToken

    setCreatingFlagKey(definition.key)
    setFlagError(null)

    let defaultValue: boolean

    try {
      const response = await epsClient.get<ConfigDefinition>(
        `/admin/configs/definitions/${definition.key}`
      )
      if (flowToken.cancelled) {
        return
      }
      const schemaDefault = response.data?.default
      defaultValue = typeof schemaDefault === 'boolean' ? schemaDefault : false
    } catch {
      if (flowToken.cancelled) {
        return
      }
      defaultValue = false
    }

    mutate(
      { customerId, key: definition.key, value: defaultValue },
      {
        onSuccess: () => {
          activeFlagFlowRef.current = null
          setCreatingFlagKey(null)
          message.success(`"${definition.title}" flag added successfully.`)
          onClose()
        },
        onError: (err: unknown) => {
          activeFlagFlowRef.current = null
          setCreatingFlagKey(null)
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to create flag.'
          setFlagError(errorMessage)
        },
      }
    )
  }

  function handleConfigClick(definition: DefinitionManifestEntry) {
    onSelectConfig(definition)
  }

  function handleClose() {
    // Block close only while the mutation is actually in-flight on the server.
    // During the preceding schema fetch we let the user cancel and mark the
    // in-flight flow so the subsequent `mutate` call is skipped.
    if (isMutating) {
      return
    }
    if (activeFlagFlowRef.current !== null) {
      activeFlagFlowRef.current.cancelled = true
      activeFlagFlowRef.current = null
      setCreatingFlagKey(null)
    }
    onClose()
  }

  function handleAfterClose() {
    setSearchTerm('')
    setCreatingFlagKey(null)
    setFlagError(null)
    activeFlagFlowRef.current = null
    onAfterClose()
  }

  function renderContent() {
    if (groupedByCategory.length === 0) {
      return (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <Text style={{ color: '#5B7083' }}>
            No configurations match your search.
          </Text>
        </div>
      )
    }

    return (
      <Stack space={20}>
        {flagError && (
          <Alert
            type="error"
            message="Failed to create flag"
            description={flagError}
            showIcon
            closable
            onClose={() => setFlagError(null)}
          />
        )}
        {groupedByCategory.map(([category, definitions]) => (
          <Stack key={category} space={8}>
            <Text
              style={{
                color: '#5B7083',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              {category}
            </Text>
            {definitions.map((definition) => (
              <DefinitionRow
                key={definition.key}
                definition={definition}
                isDisabled={isCreatingFlag}
                onClick={() => {
                  if (definition.type === 'feature-flag') {
                    void handleFlagClick(definition)
                  } else {
                    handleConfigClick(definition)
                  }
                }}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    )
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={560}
      title="Add configuration"
      afterClose={handleAfterClose}
      closable={!isMutating}
      maskClosable={!isMutating}
    >
      <Spin
        spinning={isCreatingFlag}
        indicator={<LoadingOutlined spin />}
        tip={isMutating ? 'Creating flag…' : 'Loading schema…'}
      >
        <Stack space={16}>
          <Input.Search
            placeholder="Search by name, key, or description…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            allowClear
            disabled={isCreatingFlag}
          />
          <div style={{ maxHeight: 440, overflowY: 'auto' }}>
            {renderContent()}
          </div>
        </Stack>
      </Spin>
    </Modal>
  )
}
